import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import { Subject } from 'rxjs';
import {IHolidayDetailSolution} from '../../holiday-detail-page/interfaces/iholiday-detail-solution';
import {LoadingService} from '../../../../shared/services/loading.service';
import {HolidayDetailService} from '../../holiday-detail-page/services/holiday-detail.service';
import {HolidayBookingService} from '../../holiday-booking-page/services/holiday-booking.service';
import * as dayjs from 'dayjs';
import {takeUntil} from 'rxjs/operators';
import { HolidayPrefetchService } from 'src/app/modules/shared/services/holiday-prefetch.service';
import { IPrefetchDateParams } from 'src/app/modules/shared/interfaces/iprefetch-date-params';

interface IPriceGridMonth {
  monthIndex: number,
  year: number,
  monthLabel?: string,
}

interface IPriceGridParentTypes {
  soloSoggiorno: boolean,
  voloSoggiorno: boolean,
  naveSoggiorno: boolean,
}

@Component({
  selector: 'holiday-detail-generic-page-price-grid',
  templateUrl: './holiday-detail-generic-page-price-grid.component.html',
  styleUrls: ['./holiday-detail-generic-page-price-grid.component.scss']
})
export class HolidayDetailGenericPagePriceGridComponent implements OnInit, OnChanges, OnDestroy {

  public readonly monthLabelsValue = [
    'gennaio',
    'febbraio',
    'marzo',
    'aprile',
    'maggio',
    'giugno',
    'luglio',
    'agosto',
    'settembre',
    'ottobre',
    'novembre',
    'dicembre'
  ];

  @Input() allAvailableDates: Date[];
  @Input() inputMonthSolutions: IHolidayDetailSolution[];
  @Input() accomodationParentId: number;

  @Output() newMonthSelectedEvent: EventEmitter<Date> = new EventEmitter<Date>();

  availableMonths: IPriceGridMonth[];
  selectedMonth: IPriceGridMonth;

  availableParentTypes: IPriceGridParentTypes = {
    soloSoggiorno: false,
    voloSoggiorno: false,
    naveSoggiorno: false,
  };

  allSolutionsForSelectedMonth: IHolidayDetailSolution[];

  allSolutionsToShow: IHolidayDetailSolution[] = []; //  Quelle che matchano con notti e tipo di viaggio selezionato.

  allAirportsCodesForSelectedMonth: number[];
  allDurationsForSelectedMonth: number[];

  availableDatesForSelectedMonth: Date[];

  selectedDuration: number;

  selectedTransportMode: 'NOTRANSPORT' | 'FLIGHT' | 'SHIP' = 'NOTRANSPORT';

  isLoading: boolean = false;

  selectedSolution: IHolidayDetailSolution;

  public bestSolution: IHolidayDetailSolution;
  private unsubscribe$ = new Subject();


  public config = {
    breakpoints: {
      320: {
        slidesPerView: 1,
        simulateTouch: true
      },
      480: {
        slidesPerView: 1,
        simulateTouch: true
      },
      768: {
        slidesPerView: 3,
        simulateTouch: true
      },
      992: {
        slidesPerView: 4,
        simulateTouch: false,
        allowTouchMove: false
      },
      1200: {
        slidesPerView: 5,
        simulateTouch: false,
        allowTouchMove: false
      },
      1600: {
        slidesPerView: 5,
        simulateTouch: false,
        allowTouchMove: false
      }
    },
    slidesPerView: 5,
    centeredSlides: false,
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    }
  };

  public index;

  constructor(
    private _holidayPrefetchService: HolidayPrefetchService,
    private _holidayDetailService: HolidayDetailService,
    private _loadingService: LoadingService,
    private _holidayBookingService: HolidayBookingService
  ) {
  }

  ngOnInit(): void {
    this._holidayBookingService.holidayDetailLoadingStatus$
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((loadingStatus) => {
        this.isLoading = loadingStatus.isLoadingDetail || loadingStatus.isLoadingLivePrices || loadingStatus.isDebounceing;
      });

    this._holidayBookingService.selectedSolution$
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((selectedSolution: IHolidayDetailSolution) => {
        this.selectedSolution = selectedSolution;

        if(!this.selectedSolution) {
          return;
        }
        this.selectedSolution.isShownPriceLive = true;

      });
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.allAvailableDates && this.allAvailableDates?.length) {
      await this.filterAvailableDates();
      this.setAvailableMonths();
    }

    if (changes.inputMonthSolutions && this.inputMonthSolutions?.length) {
      this.handleNewHolidaySolutions(this.inputMonthSolutions);

      if (this.selectedMonth) {
        return;
      }

      let selectedMonthIndex = this.availableMonths ? this.availableMonths[0]?.monthIndex : null;

      // serve una data qualsiasi fra quelle restituite dalla dettaglio iniziale per settare il selectedMonth iniziale
      const anySolutionWithValidDateFrom = (
        this.allSolutionsToShow?.length ?
          this.allSolutionsToShow :
          this.allSolutionsForSelectedMonth
      )
        ?.find((sol: IHolidayDetailSolution) => {
          return sol?.dateFrom && dayjs(sol?.dateFrom).isValid()
        });

      selectedMonthIndex = anySolutionWithValidDateFrom.dateFrom.getMonth() ?? selectedMonthIndex;
      this.selectedMonth = this.availableMonths ? this.availableMonths.find((month: IPriceGridMonth) => month.monthIndex === selectedMonthIndex) || this.availableMonths[0] : null;

    }

  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  filterAvailableDates(){
    const prefetchDateParams = {} as IPrefetchDateParams;
    prefetchDateParams.accommodations = [this.accomodationParentId];

    return this._holidayPrefetchService.prefetchDate(prefetchDateParams).toPromise()
      .then((dates: string[]) => {
        if (!dates?.length) {
          throw new Error('No available dates!');
        }
        let matchDates = [];
        this.allAvailableDates.forEach(date => {
          dates.forEach((stringDate) => {
            const date1 = new Date(stringDate);
            date1.setHours(0,0,0,0);
            const date2 = new Date(date);
            date2.setHours(0,0,0,0);
            if(date1.getTime() === date2.getTime()) {
              matchDates.push(stringDate);
            }
          })
        })
        this.allAvailableDates = [...new Set(matchDates)]
          ?.map((stringDate: string) => {
            return dayjs(stringDate, 'YYYY-MM-DD').isValid() ?
              dayjs(stringDate, 'YYYY-MM-DD').toDate() :
              null;
          })
          ?.filter((validDate: Date) => validDate);
      })
      .catch((err: Error) => {
        console.warn(err);
      });
  }

  //  Setta array formattato di mesi disponibili a partire dalle date restituite da preferchDate (ed ottenute tramite input)
  public setAvailableMonths() {
    this.availableMonths = this.allAvailableDates.map((availableDate: Date) => {
      return {
        monthIndex: availableDate.getMonth(),
        year: availableDate.getFullYear(),
      };
    })
      ?.filter((_tmpMonthObjOuter: IPriceGridMonth, index, selfArray) => {
        const foundIndexInSelfArray = selfArray
          ?.findIndex((_tmpMonthObjInner: IPriceGridMonth) => {
            return _tmpMonthObjInner.year === _tmpMonthObjOuter.year && _tmpMonthObjInner.monthIndex === _tmpMonthObjOuter.monthIndex;
          });

        return index === foundIndexInSelfArray;
      })
      ?.map((_tmpMonthObjArray: IPriceGridMonth) => {
        _tmpMonthObjArray.monthLabel = this.monthLabelsValue[_tmpMonthObjArray.monthIndex]?.slice(0, 3);
        return _tmpMonthObjArray;
      });
  }

  setSelectedMonth(month: IPriceGridMonth) {
    if (this.selectedMonth && month.monthIndex === this.selectedMonth?.monthIndex) {
      return;
    }
    this.allSolutionsToShow = [];
    this.selectedMonth = month;

    const middleDateInSelectedMonth = dayjs()
      .year(this.selectedMonth.year)
      .month(this.selectedMonth.monthIndex)
      .date(15)
      .toDate();

    setTimeout(() => {
      this.newMonthSelectedEvent.emit(middleDateInSelectedMonth);
    }, 0);
  }

  public handleNewHolidaySolutions(newHolidaySolutions: IHolidayDetailSolution[]) {

    this.allSolutionsForSelectedMonth = newHolidaySolutions;

    //  Reset all
    this.allSolutionsToShow = [];
    this.allAirportsCodesForSelectedMonth = [];
    this.allDurationsForSelectedMonth = [];

    this.allAirportsCodesForSelectedMonth = this.allSolutionsForSelectedMonth
      ?.map((solution: IHolidayDetailSolution) => solution.airport)
      ?.filter((airportCode: number, index, selfArray) => {
        return airportCode && selfArray.indexOf(airportCode) === index;
      });

    this.allDurationsForSelectedMonth = this.allSolutionsForSelectedMonth
      ?.map((solution: IHolidayDetailSolution) => solution.nights)
      ?.filter((nights: number, index, selfArray) => {
        return nights && selfArray.indexOf(nights) === index;
      })
      ?.sort((durationA: number, durationB: number) => durationA - durationB);

    //  Selezione durata
    if (this.allDurationsForSelectedMonth?.length) {
      if (!this.selectedDuration || !this.allDurationsForSelectedMonth.includes(this.selectedDuration)) {
        this.setSelectedDuration(this.allDurationsForSelectedMonth[0]);
      }
      this.setSelectedDuration(this.selectedDuration);
    }

  }


  setSelectedDuration(duration: number, checkTrasports: boolean = true) {
    this.selectedDuration = duration;

    //  Filtraggio per durata
    this.allSolutionsToShow = this.allSolutionsForSelectedMonth
      ?.filter((solution: IHolidayDetailSolution) => solution.nights === this.selectedDuration);
    if (!checkTrasports) {
      return;
    }
    this.checkTransportSelection();
  }

  private checkTransportSelection() {

    const soloSoggiorno = this.allSolutionsToShow?.some((solution: IHolidayDetailSolution) => this.doesSolutionMatchTransportType(solution, 'NOTRANSPORT'));
    const voloSoggiorno = this.allSolutionsToShow?.some((solution: IHolidayDetailSolution) => this.doesSolutionMatchTransportType(solution, 'FLIGHT'));
    const naveSoggiorno = this.allSolutionsToShow?.some((solution: IHolidayDetailSolution) => this.doesSolutionMatchTransportType(solution, 'SHIP'));

    this.availableParentTypes = {
      soloSoggiorno,
      voloSoggiorno,
      naveSoggiorno,
    };

    let selectedTransportMode = this.selectedTransportMode || 'NOTRANSPORT';  //TODO perché?

    if ( selectedTransportMode === 'NOTRANSPORT' && !this.availableParentTypes.soloSoggiorno) {
      selectedTransportMode = 'FLIGHT';
    }
    if ( selectedTransportMode === 'FLIGHT' && !this.availableParentTypes.voloSoggiorno) {
      selectedTransportMode = 'SHIP';
    }
    if ( selectedTransportMode === 'SHIP' && !this.availableParentTypes.naveSoggiorno) {
      selectedTransportMode = 'NOTRANSPORT';
    }

    this.setSelectedParentType(selectedTransportMode);

  }

  setSelectedParentType(parentType: 'FLIGHT' | 'NOTRANSPORT' | 'SHIP') {

    this.selectedTransportMode = parentType;

    this.allSolutionsToShow = this.allSolutionsToShow
      ?.filter((solution: IHolidayDetailSolution) => {
        return this.doesSolutionMatchTransportType(solution, this.selectedTransportMode);
      });

    this.updateAvailableDates();

  }

  private doesSolutionMatchTransportType(solution: IHolidayDetailSolution, transportType: 'FLIGHT' | 'NOTRANSPORT' | 'SHIP'): boolean {
    switch (transportType) {
      case 'FLIGHT':
        return solution.airport && solution.airport > 1000000 && solution.airport < 2000000;
      case 'NOTRANSPORT':
        return !solution.airport;
      case 'SHIP':
        return solution.airport && solution.airport > 2000000 && solution.airport < 3000000;
    }
  }

  public onClickOnParentType(transportSelection) {
    this.setSelectedDuration(this.selectedDuration, false);
    this.setSelectedParentType(transportSelection);
  }

  private updateAvailableDates() {

    this.availableDatesForSelectedMonth = [];

    this.allSolutionsToShow
      ?.forEach((solution: IHolidayDetailSolution) => {
        const alreadyIn = this.availableDatesForSelectedMonth.some((date: Date) => dayjs(date).isSame(solution.dateFrom, 'day'));
        if (alreadyIn) {
          return;
        }
        this.availableDatesForSelectedMonth.push(solution.dateFrom);
      });

    this.availableDatesForSelectedMonth.sort((dateA: Date, dateB: Date) => {
      return dayjs(dateA).isAfter(dateB) ? 1 : -1;
    });

    if(this.allSolutionsToShow?.length){
      this.bestSolution = this.findBestSolution(this.allSolutionsToShow);
      if(this.bestSolution){
        this.setSelectedSolution(this.bestSolution);
      }
    }

  }

  private findBestSolution(solutions: IHolidayDetailSolution[]): IHolidayDetailSolution{
    return solutions.reduce((prev, current) => {
      if (!prev || current.price < prev.price) {
        return current;
      }
      return prev;
    }, null);
  }

  setSelectedSolution(solution: IHolidayDetailSolution) {
    this._holidayBookingService.selectedSolution$.next(solution);
  }

}
